import React, { useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Autocomplete, TextField, FormControl, FormLabel } from '@mui/material'
import { getLabel } from '@/components/common/utils'
import { useGetCities } from '@/hooks'
import { normalizeCityInput } from '@/utils'

export const CityAutocompleteField = ({
  id,
  value = '',
  onChange,
  error = false,
  helperText = '',
  required = true,
  inputRef,
  onFocus,
  onBlur,
}) => {
  const { cities, loadingCities, getCities } = useGetCities()
  const [cityListOpen, setCityListOpen] = useState(false)

  useEffect(() => {
    getCities()
  }, [getCities])

  const matchingCities = useMemo(() => {
    const query = (value ?? '').trim().toLowerCase()
    if (!query) return cities
    return cities.filter(city => String(city).toLowerCase().includes(query))
  }, [cities, value])

  const canShowCityList = matchingCities.length > 0

  const updateMatchesOpenState = nextCity => {
    const query = nextCity.trim().toLowerCase()
    const hasMatches = query
      ? cities.some(city => String(city).toLowerCase().includes(query))
      : cities.length > 0
    setCityListOpen(hasMatches)
  }

  return (
    <FormControl fullWidth error={error}>
      <FormLabel sx={{ mb: 0.25 }}>{getLabel('City', required)}</FormLabel>
      <Autocomplete
        id={id}
        freeSolo
        options={cities}
        loading={loadingCities}
        value={value || ''}
        open={cityListOpen && canShowCityList}
        onOpen={() => {
          if (canShowCityList) setCityListOpen(true)
        }}
        onClose={() => setCityListOpen(false)}
        slotProps={{
          listbox: {
            sx: { maxHeight: 220 },
          },
        }}
        onChange={(_, selected) => {
          onChange(normalizeCityInput(String(selected ?? '')))
          setCityListOpen(false)
        }}
        onInputChange={(_, inputValue, reason) => {
          if (reason === 'input' || reason === 'clear') {
            const nextCity = normalizeCityInput(inputValue)
            onChange(nextCity)
            updateMatchesOpenState(nextCity)
          }
        }}
        renderInput={({ InputLabelProps: _labelProps, ...params }) => (
          <TextField
            {...params}
            placeholder='City'
            error={error}
            helperText={helperText}
            inputRef={inputRef}
            onFocus={event => {
              if (canShowCityList) setCityListOpen(true)
              onFocus?.(event)
            }}
            onBlur={event => {
              setCityListOpen(false)
              onBlur?.(event)
            }}
            inputProps={{
              ...params.inputProps,
              maxLength: 100,
              autoComplete: 'address-level2',
            }}
          />
        )}
      />
    </FormControl>
  )
}

CityAutocompleteField.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
}

export default CityAutocompleteField
